import React, { useState, useEffect } from 'react';

interface ActionSectionProps {
  onRecordClick: () => void;
}

const familyMembers = [
  { word: 'madre', fontClass: 'font-hand-madre' },
  { word: 'padre', fontClass: 'font-hand-padre' },
  { word: 'abuela', fontClass: 'font-hand-abuela' },
  { word: 'abuelo', fontClass: 'font-hand-abuelo' },
];

const ActionSection: React.FC<ActionSectionProps> = ({ onRecordClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % familyMembers.length);
    }, 1300);
    return () => clearInterval(interval);
  }, []);

  const current = familyMembers[currentIndex];

  const RotatingWord = () => (
    <span className="whitespace-nowrap">
      tu{' '}
      <span className="relative inline-block align-bottom pr-1">
        <span className="invisible font-hand-padre not-italic select-none" aria-hidden="true">padre</span>
        <span className={`text-rose-500 ${current.fontClass} not-italic absolute left-0 bottom-0`}>
          {current.word}
        </span>
      </span>?
    </span>
  );

  return (
    <section className="py-8 md:py-16 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: stacked single column */}
        <div className="lg:hidden max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block bg-stone-100 px-3 py-1 rounded-full text-[10px] font-semibold text-stone-500 mb-4 tracking-wider uppercase">
              Prueba con una pregunta de verdad.
            </div>
            <h3 className="text-3xl font-serif font-semibold text-stone-900 leading-tight">
              ¿Sabrías qué respondería <RotatingWord />
            </h3>
          </div>

          {/* Card */}
          <OverlayCard onRecordClick={onRecordClick} />
        </div>

        {/* Desktop: two-column layout */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-12 xl:gap-16 items-start max-w-6xl mx-auto">
          {/* Left: text */}
          <div className="space-y-6">
            <div className="inline-block bg-stone-100 px-3 py-1 rounded-full text-xs font-semibold text-stone-500 tracking-wider uppercase">
              Prueba con una pregunta de verdad.
            </div>
            <h3 className="text-3xl xl:text-4xl font-serif font-semibold text-stone-900 leading-tight">
              ¿Sabrías qué respondería <RotatingWord />
            </h3>
            <p className="text-lg text-stone-600 leading-relaxed">
              Esta es una de las preguntas que Ethernal te ayuda a hacer. Ponla a prueba — pregúntaselo este domingo.
            </p>
          </div>

          {/* Right: overlay card */}
          <OverlayCard onRecordClick={onRecordClick} />
        </div>
      </div>
    </section>
  );
};

const OverlayCard: React.FC<{ onRecordClick: () => void }> = ({ onRecordClick }) => (
  <div className="bg-white rounded-2xl shadow-2xl shadow-stone-300/50 border border-stone-200 overflow-hidden">
    {/* Card header */}
    <div className="bg-stone-50 border-b border-stone-100 px-6 py-3 flex items-center justify-between">
      <span className="text-xs font-bold tracking-wider uppercase text-rose-500">
        Tema #1
      </span>
      <span className="text-xs text-stone-400">1 de 42</span>
    </div>

    {/* Card body */}
    <div className="p-6 md:p-8">
      {/* Question */}
      <div className="mb-8">
        <p className="text-xs text-stone-400 uppercase tracking-wider mb-2">Háblame de:</p>
        <h4 className="text-xl md:text-2xl font-serif italic text-stone-800 leading-relaxed">
          ¿Cuál es el antepasado más antiguo que conoces?
        </h4>
      </div>

      {/* Guiding questions */}
      <div className="bg-stone-50 rounded-xl p-4 mb-8 border border-stone-100">
        <p className="text-xs text-stone-400 uppercase tracking-wider mb-3">Preguntas guía</p>
        <ul className="space-y-2 text-sm text-stone-600">
          <li className="flex gap-2">
            <span className="text-rose-400 shrink-0">1.</span>
            ¿Cómo se llamaba y de dónde era?
          </li>
          <li className="flex gap-2">
            <span className="text-rose-400 shrink-0">2.</span>
            ¿Qué historias te contaban sobre esa persona?
          </li>
          <li className="flex gap-2">
            <span className="text-rose-400 shrink-0">3.</span>
            ¿Qué recuerdos o costumbres se han mantenido en la familia?
          </li>
        </ul>
      </div>

      {/* CTA */}
      <div className="flex justify-center">
        <button
          onClick={onRecordClick}
          className="bg-rose-500 text-white px-8 py-3.5 rounded-full text-base font-semibold hover:bg-rose-600 transition shadow-lg hover:shadow-rose-500/30"
        >
          Hacer esta pregunta
        </button>
      </div>
    </div>
  </div>
);

export default ActionSection;
